import { useEffect } from 'react';
import { Shield, Trophy, Users } from 'lucide-react';
import { motion } from 'motion/react';

import type { ClientSignInLayoutProps, FlashNotification } from '@/types/pages';

import { Footer } from '@/components/AppLayoutClient/Footer';
import { Header } from '@/components/AppLayoutClient/Header';
import { Toaster } from '@/components/ui/sonner';

import { DisplayToast } from '@/helpers';

import { AnimatedHeroText } from './AnimatedHeroText';

const features = [
  {
    icon: Trophy,
    text: 'Тренування та класи',
    iconClass: 'text-primary-soft-foreground',
    bgClass: 'bg-primary/10',
  },
  {
    icon: Users,
    text: 'Спільнота та події',
    iconClass: 'text-primary-soft-foreground',
    bgClass: 'bg-primary/10',
  },
  {
    icon: Shield,
    text: 'Купівля абонементів онлайн',
    iconClass: 'text-primary-soft-foreground',
    bgClass: 'bg-primary/10',
  },
];

export const ClientSignInLayout = (props: ClientSignInLayoutProps) => {
  const { commonData, children } = props;

  useEffect(() => {
    const flashRoot = document.getElementById('flash-root');
    const { flash } = JSON.parse(
      flashRoot?.getAttribute('data') || '{ "flash": [] }',
    ) as {
      flash: FlashNotification[];
    };

    if (flash.length > 0) {
      flash.forEach((notification) => {
        if (notification.type === 'alert' || notification.type === 'error') {
          DisplayToast.error(notification.text);
        } else {
          DisplayToast.notice(notification.text);
        }
      });
    }
  }, []);

  const headerNavigation = {
    logo_url: commonData.logo_url,
    home_page_url: '',
    my_visits_url: null,
    dashboard_url: null,
    auth_url: '',
    my_booking_url: '',
    booking_types_url: commonData.booking_types_url,
    booking_class_url: '',
    booking_spot_event_url: '',
    booking_url: '',
    online_booking_url: commonData.online_booking_url ?? null,
    my_reservations_url: null,
    my_subscription_url: '',
    subscriptions_url: '',
    tickets_url: '',
    cards_url: '',
    subscription_cards_url: '',
    client_subscription_url: '',
    certificates_url: commonData.certificates_url ?? null,
    client_certificates_url: null,
    online_certificates_url: commonData.online_certificates_url,
  };

  const { company, agreement_file_url } = commonData;

  return (
    <div className="flex min-h-screen flex-col">
      <Header
        navigation={headerNavigation}
        clientData={{
          client_name: null,
          log_out_props: { token: '', url: '' },
        }}
        payment_maintenance_warning={commonData.payment_maintenance_warning}
      />
      <div className="flex flex-1 flex-col md:flex-row">
        {/* Left side - Brand section with purple gradient */}
        <div className="md:bg-primary/10 relative flex flex-1 overflow-hidden md:w-1/2">
          {/* Animated background blobs */}
          <motion.div
            className="bg-primary/10 pointer-events-none absolute bottom-40 -left-20 size-96 rounded-full blur-3xl md:top-20"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
          />
          <motion.div
            className="bg-primary/10 pointer-events-none absolute -right-20 bottom-20 size-80 rounded-full blur-3xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.3, ease: 'easeOut' }}
          />

          <div className="flex flex-1 flex-col justify-between p-4 sm:p-6 md:p-8 lg:p-12">
            {/* Mobile form */}
            <motion.div
              className="flex items-center justify-center p-2 md:hidden md:p-6 lg:p-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="w-full md:max-w-md">{children}</div>
            </motion.div>

            {/* Hero content */}
            <div className="space-y-8 py-8">
              <AnimatedHeroText />

              {/* Features with stagger animation */}
              <div className="space-y-3">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={feature.text}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: 0.5 + index * 0.1,
                        ease: 'easeOut',
                      }}
                    >
                      <motion.div
                        className={`${feature.bgClass} flex size-10 shrink-0 items-center justify-center rounded-lg`}
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: 'spring', stiffness: 400 }}
                      >
                        <Icon className={`${feature.iconClass} size-5`} />
                      </motion.div>
                      <span className="text-foreground text-sm">
                        {feature.text}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Public offer link */}
            {agreement_file_url && (
              <motion.div
                className="border-border border-t pt-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              >
                <motion.a
                  href={agreement_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-soft-foreground hover:text-primary-soft-foreground/80 inline-flex items-center gap-1 text-sm transition-colors"
                  whileHover={{ x: 4 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  Публічна оферта
                  <span className="text-xs">→</span>
                </motion.a>
              </motion.div>
            )}
          </div>
        </div>

        {/* Right side - Auth form */}
        <motion.div
          className="hidden flex-1 items-center justify-center p-6 md:flex lg:p-12"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
        >
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {children}
          </motion.div>
        </motion.div>
      </div>
      <Footer
        footer_logo_url={commonData.footer_logo_url}
        company_base_settings={{
          email: company.base_settings.email || null,
          name: company.name || null,
          phone: company.base_settings.phone || null,
          work_time: company.base_settings.work_time || null,
          adress: company.base_settings.adress || null,
        }}
      />
      <Toaster />
    </div>
  );
};
